import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { PlayArrow, Stop, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

export interface Fund {
  date: string;
  fundName: string;
  totalRaised: number;
  totalRaisedDisplay: string;
  published: boolean;
  status: string;
  campaignId: number;
  streamId: number | null;
  donationCount: number;
}

interface FundsTableProps {
  rows: Fund[];
  togglingCampaignId: number | null;
  onToggleStatus: (fund: Fund) => void;
}

const FundsTable = ({
  rows,
  togglingCampaignId,
  onToggleStatus,
}: FundsTableProps) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((fund) => fund.fundName.toLowerCase().includes(q));
  }, [rows, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const paginatedRows = useMemo(
    () =>
      filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          CHEMA Funds Listing
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search by fund name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              minWidth: 300,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
          <Button
            variant="outlined"
            disabled={!hasPermission(PERMISSIONS.DONATION_VIEW)}
            sx={{
              textTransform: "none",
              color: "text.primary",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            Generate Report
          </Button>
        </Box>
      </Box>

      <Table sx={{ "& td, & th": { borderBottom: "none" } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f8f9fa" }}>
            <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Fund Name</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Donations</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Total Raised</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Manage</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  {searchQuery
                    ? "No funds match your search"
                    : "No CHEMA funds found"}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedRows.map((fund) => (
              <TableRow
                key={fund.campaignId}
                hover
                sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } }}
                onClick={() => navigate(`/chema/${fund.campaignId}`)}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: fund.totalRaised > 0 ? "#10b981" : "#9ca3af",
                      }}
                    />
                    {fund.date}
                  </Box>
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "primary.main",
                    },
                  }}
                >
                  {fund.fundName}
                </TableCell>

                <TableCell>
                  <Chip
                    label={fund.donationCount}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: fund.donationCount > 0 ? "#ecfdf5" : "#f3f4f6",
                      color: fund.donationCount > 0 ? "#065f46" : "#6b7280",
                    }}
                  />
                </TableCell>

                <TableCell sx={{ fontWeight: "bold" }}>
                  {fund.totalRaisedDisplay}
                </TableCell>

                <TableCell>
                  <Chip
                    label={fund.published ? "Enabled" : "Disabled"}
                    size="small"
                    sx={{
                      bgcolor: fund.published ? "#d1fae5" : "#fee2e2",
                      color: fund.published ? "#065f46" : "#991b1b",
                      fontWeight: 500,
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Button
                    variant="outlined"
                    startIcon={
                      togglingCampaignId === fund.campaignId ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : fund.published ? (
                        <Stop />
                      ) : (
                        <PlayArrow />
                      )
                    }
                    size="small"
                    disabled={
                      !hasPermission(PERMISSIONS.DONATION_CHANGE) ||
                      togglingCampaignId === fund.campaignId
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleStatus(fund);
                    }}
                    sx={{
                      textTransform: "none",
                      borderRadius: 4,
                      minWidth: 90,
                      borderColor: fund.published ? "#ef4444" : "#10b981",
                      color: fund.published ? "#ef4444" : "#10b981",
                      bgcolor: fund.published ? "#fef2f2" : "#ecfdf5",
                      "&:hover": {
                        borderColor: fund.published ? "#dc2626" : "#059669",
                        bgcolor: fund.published ? "#fee2e2" : "#d1fae5",
                      },
                    }}
                  >
                    {fund.published ? "Stop" : "Start"}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Paper>
  );
};

export default FundsTable;
